import * as React from 'react'

import CheckboxField from './fields/CheckboxField'
import ColorField from './fields/ColorField'
import CompanyField from './fields/CompanyField'
import DateTimeField from './fields/DateTimeField'
import FileField from './fields/FileField'
import HierarchyField from './fields/HierarchyField'
import IconField from './fields/IconField'
import LocationField from './fields/LocationField'
import NumberField from './fields/NumberField'
import ParentField from './fields/ParentField'
import SelectField from './fields/SelectField'
import TextAreaField from './fields/TextAreaField'
import TextField from './fields/TextField'
import UserField from './fields/UserField'
import { FieldConfig, FieldType, FileUploadFn } from './types'

interface DynamicFieldRendererProps {
  field: FieldConfig
  control: any
  error?: string
  hideHelperText?: boolean
  fileUploadFn?: FileUploadFn
  onFieldChange?: (
    fieldName: string,
    updatedField: Partial<FieldConfig>,
  ) => void
}

export default function DynamicFieldRenderer({
  field,
  control,
  error,
  hideHelperText,
  fileUploadFn,
  onFieldChange,
}: DynamicFieldRendererProps) {
  switch (field.type) {
    case FieldType.TEXT:
      return (
        <TextField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
        />
      )
    case FieldType.TEXTAREA:
      return (
        <TextAreaField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
        />
      )
    case FieldType.NUMBER:
      return (
        <NumberField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
        />
      )
    case FieldType.SELECT:
      return (
        <SelectField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
          onOptionsChanged={options => {
            onFieldChange?.(field.name, { options })
          }}
        />
      )
    case FieldType.HIERARCHY:
      return (
        <HierarchyField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
        />
      )
    case FieldType.DATETIME:
      return (
        <DateTimeField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
        />
      )
    case FieldType.CHECKBOX:
      return (
        <CheckboxField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
        />
      )
    case FieldType.USER:
      return (
        <UserField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
        />
      )
    case FieldType.COMPANY:
      return (
        <CompanyField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
        />
      )
    case FieldType.LOCATION:
      return (
        <LocationField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
        />
      )
    case FieldType.FILE:
      if (!fileUploadFn) {
        throw new Error('File fields require a file upload function')
      }
      return (
        <FileField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
          uploadFn={fileUploadFn}
        />
      )
    case FieldType.COLOR:
      return <ColorField field={field} control={control} error={error} />
    case FieldType.ICON:
      return <IconField field={field} control={control} error={error} />
    case FieldType.PARENT:
      return (
        <ParentField
          field={field}
          control={control}
          error={error}
          hideHelperText={hideHelperText}
        />
      )
    default:
      console.warn(`No component found for field type: ${(field as any).type}`)
      return null
  }
}
